import { Injectable } from '@angular/core';
import { IamService } from '@app/core/services/iam.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin } from 'rxjs';
import { catchError, map, switchMap, concatMap, mergeMap, withLatestFrom, defaultIfEmpty } from 'rxjs/operators';
import { GroupsActions } from '../actions';
import * as fromRoot from '@app/reducers';
import * as fromGroups from '../reducers/groups.reducer';
import { select, Store } from '@ngrx/store';
import { TokenService } from '@app/core/services/token.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

const SCIMGroupName = 'SCIM';

@Injectable()
export class GroupsEffects {

  loadGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroups),
      switchMap(({ teamId }) => {
        return this.iamService.listTeamGroups(teamId).pipe(
          map(({ groups }) => {
            const groupsWithoutSCIMGroup = groups.filter(group => group.name !== SCIMGroupName);
            return GroupsActions.LoadGroupsSuccess({ groups: groupsWithoutSCIMGroup });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupsFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAttributableRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadAttributableRoles),
      switchMap(({ teamId }) => {
        return this.iamService.listTeamRoles(teamId).pipe(
          map(({ roles }) => {
            return GroupsActions.LoadAttributableRolesSuccess({ roles });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupsFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupDetails),
      switchMap(({ teamId, id }) => {
        return this.iamService.findTeamGroup(teamId, id).pipe(
          map(({ group }) => {
            return GroupsActions.LoadGroupDetailsSuccess({ group });
          }),
          catchError(error => { this.router.navigateByUrl('/account/team/' + teamId + '/groups'); return of(GroupsActions.LoadGroupDetailsFailure({ error: error.message })); }
          )
        );
      }))
  );

  updateGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.UpdateGroup),
      switchMap(({ teamId, group }) => {
        return this.iamService.updateTeamGroup(teamId, group.id, group).pipe(map(({ group }) => {
          return GroupsActions.UpdateGroupSuccess({ group });
        }),
          catchError(error =>
            of(GroupsActions.UpdateGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupRoles),
      switchMap((action) => {
        return this.iamService.listTeamGroupRoles(action.teamId, action.id).pipe(
          map(({ roles }) => {
            const parsedRoles = roles.map(role => ({ ...role, translatedName: this.translateService.instant(role.name.split('$')[1].toUpperCase()) }));
            return GroupsActions.LoadGroupRolesSuccess({ roles: parsedRoles });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupRolesFailure({ error: error.message }))
          ));
      })
    )
  );

  loadGroupMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadGroupMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]: any[]) => {
        return this.iamService.listGroupUsers(action.id).pipe(
          map(res => {
            const SCIMUserName = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
            const { users } = res;
            const usersWithoutSCIMUser = users.filter(user => user.username !== SCIMUserName);
            return GroupsActions.LoadGroupMembersSuccess({ members: usersWithoutSCIMUser });
          }),
          catchError(error =>
            of(GroupsActions.LoadGroupMembersFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.LoadAccountMembers),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]: any[]) => {
        return this.iamService.listUsers().pipe(
          map(res => {
            const SCIMUserName = this.tokenService.getAccountFromToken(identity.token.token) + '$scim';
            const { users } = res;
            const usersWithoutSCIMUser = users.filter(user => user.username !== SCIMUserName);
            return GroupsActions.LoadAccountMembersSuccess({ members: usersWithoutSCIMUser });
          }),
          catchError(error =>
            of(GroupsActions.LoadAccountMembersFailure({ error: error.message }))
          ));
      })
    )
  );

  attachRoleToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.AttachRoleToGroup),
      switchMap((action) => {
        return this.iamService.attachTeamGroupRole(action.teamId, action.groupId, action.role.name).pipe(map(() => {
          return GroupsActions.AttachRoleToGroupSuccess();
        }));
      })
    )
  );

  attachMemberToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.AttachMemberToGroup),
      switchMap((action) => {
        if (action?.member?.id) {
          return this.iamService.attachTeamGroupUser(action.teamId, action.groupId, action.member.id).pipe(map(() => {
            return GroupsActions.AttachMemberToGroupSuccess();
          }),
            catchError(error => of(GroupsActions.AttachMemberToGroupFailure({ error: error.message })))
          );
        } else {
          return this.iamService.createUser({ username: action.member.username }).pipe(concatMap(({ user }) => {
            return this.iamService.attachTeamGroupUser(action.teamId, action.groupId, user.id).pipe(map(() => {
              return GroupsActions.AttachMemberToGroupSuccess();
            }),
              catchError(error => of(GroupsActions.AttachMemberToGroupFailure({ error: error.message })))
            );
          }),
            catchError(error => of(GroupsActions.AttachMemberToGroupFailure({ error: error.message })))
          );
        }
      }))
  );


  detachMemberFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DetachMemberFromGroup),
      switchMap((action) => {
        return this.iamService.detachTeamGroupUser(action.teamId, action.groupId, action.userId).pipe(map(() => {
          return GroupsActions.DetachMemberFromGroupSuccess();
        }),
          catchError(error =>
            of(GroupsActions.DetachMemberFromGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  detachRoleFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DetachRoleFromGroup),
      switchMap((action) => this.iamService.detachTeamGroupRole(action.teamId, action.groupId, action.role.name).pipe(
        map(() => GroupsActions.DetachRoleFromGroupSuccess()),
        catchError(error => of(GroupsActions.DetachRoleFromGroupFailure({ error: error.message }))))),
    )
  );

  deleteGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.DeleteGroup),
      switchMap(({ teamId, groupId }) => {
        const reqsLinks = [
          this.iamService.listTeamGroupUsers(teamId, groupId),
          this.iamService.listTeamGroupRoles(teamId, groupId),
        ];
        return forkJoin(reqsLinks).pipe(
          defaultIfEmpty([]),
          concatMap(([{ users }, { roles }]: any[]) => {
            const reqsDetachRessources = [];
            if (users.length > 0) {
              reqsDetachRessources.push(...users.map(user => this.iamService.detachTeamGroupUser(teamId, groupId, user.id)));
            }
            if (roles.length > 0) {
              reqsDetachRessources.push(...roles.map(role => this.iamService.detachTeamGroupRole(teamId, groupId, role.name)));
            }
            if (reqsDetachRessources.length > 0) {
              return forkJoin(reqsDetachRessources).pipe(
                defaultIfEmpty([]),
                concatMap(() => {
                  return this.iamService.detachTeamFromGroup(groupId, teamId).pipe(concatMap(() => {
                    return this.iamService.deleteGroup(groupId).pipe(concatMap(() => {
                      return of(GroupsActions.DeleteGroupSuccess());
                    }));
                  }));
                }),
                catchError(error =>
                  of(GroupsActions.DeleteGroupFailure({ error: error.message }))
                ));
            }
            return this.iamService.detachTeamFromGroup(groupId, teamId).pipe(concatMap(() => {
              return this.iamService.deleteGroup(groupId).pipe(concatMap(() => {
                return of(GroupsActions.DeleteGroupSuccess());
              }),
                catchError(error =>
                  of(GroupsActions.DeleteGroupFailure({ error: error.message }))
                ));
            }));
          }),
          catchError(error =>
            of(GroupsActions.DeleteGroupFailure({ error: error.message }))
          ));
      })));

  createGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GroupsActions.CreateGroup),
      switchMap((action) => {
        return this.iamService.createGroup({ name: action.name }).pipe(
          concatMap(({ group }) => {
            return this.iamService.attachTeamToGroup(group.id, action.teamId).pipe(map(() => {
              return GroupsActions.CreateGroupSuccess({ group });
            }));
          }),
          catchError(error =>
            of(GroupsActions.CreateGroupFailure({ error: error.message }))
          ));
      })
    )
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private iamService: IamService,
    private tokenService: TokenService,
    private translateService: TranslateService,
    private store$: Store<fromRoot.State & fromGroups.State>
  ) { }

}
